import { useState, useRef, useEffect } from 'react';
import { Link } from '@remix-run/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import Location from '~/assets/icons/Location';
import { useRootLoader } from '~/utils/use-root-loader';

type Collection = {
  name: string;
  slug: string;
  children?: { name: string; slug: string }[];
};

type RootCollectionItem = {
  name: string;
  id: string;
  slug: string;
  children?: { name: string; id: string; slug: string }[] | null;
};

export function SubHeader() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const rootData = useRootLoader();

  const rawCollections: RootCollectionItem[] = rootData?.collections || [];

  const collections: Collection[] =
    rawCollections.length > 0
      ? rawCollections.map((col: RootCollectionItem) => ({
          name: col.name,
          slug: col.slug,
          // Map children with specific name and slug properties
          children:
            col.children?.map((c) => ({ name: c.name, slug: c.slug })) ?? [],
        }))
      : [];

  const toggleDropdown = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpenIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    // Hide category nav on mobile; also reduce bottom gap.
    <header className="hidden md:block w-full border-t-2 border-b-2 mb-4">
      <div className="bg-white shadow-sm">
        <div
          ref={containerRef}
          className="container mx-auto px-4 relative h-[56px] flex items-center justify-center"
        >
          <div className="absolute left-4 flex items-center gap-2">
            <Location />
            <span className="">Deliver to all</span>
          </div>

          <div className="flex ml-0 md:ml-24 space-x-5 text-gray-700 overflow-x-auto py-4 no-scrollbar sm:overflow-visible">
            {collections.map((item, index) => (
              <div key={index} className="relative flex-shrink-0">
                <div
                  className={`flex items-center space-x-1 text-sm md:text-base hover:text-black focus:outline-none ${
                    item.name === 'NEW ARRIVALS' ? 'font-bold' : 'font-normal'
                  }`}
                >
                  <Link
                    to={`/collections/${item.slug}`}
                    className="hover:underline"
                    onClick={(e) => {
                      if (item.children && item.children.length > 0) {
                        e.preventDefault();
                        toggleDropdown(index);
                      }
                    }}
                  >
                    <span>{item.name.toUpperCase()}</span>
                  </Link>

                  {item.children && item.children.length > 0 && (
                    <button
                      onClick={() => toggleDropdown(index)}
                      className="p-1 focus:outline-none"
                      aria-label={`Toggle dropdown for ${item.name}`}
                    >
                      <ChevronDownIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {item.children &&
                  item.children.length > 0 &&
                  openIndex === index && (
                    <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 mt-2 w-48 bg-white shadow-md rounded-md z-10">
                      <ul className="flex flex-col p-2">
                        {item.children.map((child: any, i) => (
                          <li key={i}>
                            <Link
                              to={`/collections/${child.slug}`}
                              className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                            >
                              {child.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
